import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import "../portfolio.css";
import SectionHeading from '../SectionHeading';
import { EASE, fadeUp, scaleIn, stagger, viewport } from '../motion';
import { skillsData } from '../../data';

// Proficiency is expressed as a named tier first, the number second — a tier is
// easier to scan across 29 tiles than 29 percentages.
const TIERS = [
  { min: 90, label: 'Advanced', dot: 'bg-indigo-600', text: 'text-indigo-700', stroke: 'stroke-indigo-600' },
  { min: 80, label: 'Proficient', dot: 'bg-blue-600', text: 'text-blue-700', stroke: 'stroke-blue-600' },
  { min: 0, label: 'Working', dot: 'bg-sky-500', text: 'text-sky-700', stroke: 'stroke-sky-500' },
];

const tierFor = (proficiency) => TIERS.find((tier) => proficiency >= tier.min);

const RADIUS = 30;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

// Level and logo share one mark: the ring reads as proficiency, the centre
// stays the thing you're actually looking for.
function ProficiencyRing({ proficiency, tier, children }) {
  return (
    <div className="relative h-[72px] w-[72px]">
      <svg className="h-full w-full -rotate-90" viewBox="0 0 72 72" aria-hidden="true">
        <circle
          cx="36"
          cy="36"
          r={RADIUS}
          fill="none"
          strokeWidth="4"
          className="stroke-slate-200"
        />
        <motion.circle
          cx="36"
          cy="36"
          r={RADIUS}
          fill="none"
          strokeWidth="4"
          strokeLinecap="round"
          className={tier.stroke}
          strokeDasharray={CIRCUMFERENCE}
          initial={{ strokeDashoffset: CIRCUMFERENCE }}
          whileInView={{ strokeDashoffset: CIRCUMFERENCE * (1 - proficiency / 100) }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">{children}</div>
    </div>
  );
}

function TierLine({ tier, proficiency }) {
  return (
    <span className="mt-auto flex items-center justify-center gap-1.5 text-[10px] tracking-wide">
      <span className={`h-1.5 w-1.5 rounded-full ${tier.dot}`}></span>
      <span className={tier.text}>{tier.label}</span>
      <span className="text-slate-300">/</span>
      <span className="tabular-nums text-slate-400">{proficiency}%</span>
    </span>
  );
}

function SkillTile({ skill, banner }) {
  const tier = tierFor(skill.proficiency);
  const label = `${skill.name}, ${tier.label}, ${skill.proficiency} percent`;

  // Wide, dark-background artwork gets a band; square logos get the ring.
  if (banner) {
    return (
      <motion.article
        layout
        aria-label={label}
        className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm transition-shadow duration-300 hover:shadow-lg"
        variants={scaleIn}
        exit={{ opacity: 0, y: -8 }}
        whileHover={{ y: -6 }}
      >
        <div className="flex h-24 items-center justify-center overflow-hidden bg-slate-900 p-3">
          <img
            src={skill.svg}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex flex-1 flex-col gap-2 p-4 text-center">
          <h4 className="text-sm font-semibold leading-tight text-slate-800">{skill.name}</h4>
          <TierLine tier={tier} proficiency={skill.proficiency} />
        </div>
      </motion.article>
    );
  }

  return (
    <motion.article
      layout
      aria-label={label}
      className="group flex flex-col items-center gap-3 rounded-2xl border border-slate-200/80 bg-white p-5 text-center shadow-sm transition-shadow duration-300 hover:shadow-lg"
      variants={scaleIn}
      exit={{ opacity: 0, y: -8 }}
      whileHover={{ y: -6 }}
    >
      <ProficiencyRing proficiency={skill.proficiency} tier={tier}>
        <img
          src={skill.svg}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="h-8 w-8 object-contain transition-transform duration-300 group-hover:scale-110"
        />
      </ProficiencyRing>

      <h4 className="text-sm font-semibold leading-tight text-slate-800">{skill.name}</h4>

      <TierLine tier={tier} proficiency={skill.proficiency} />
    </motion.article>
  );
}

function SkillsContainer() {
  const [activeCategory, setActiveCategory] = useState('All');

  const totalSkills = useMemo(
    () => skillsData.reduce((sum, category) => sum + category.skills.length, 0),
    []
  );

  const visibleCategories = useMemo(
    () =>
      activeCategory === 'All'
        ? skillsData
        : skillsData.filter((category) => category.category === activeCategory),
    [activeCategory]
  );

  const filters = ['All', ...skillsData.map((category) => category.category)];
  const countFor = (name) =>
    name === 'All'
      ? totalSkills
      : skillsData.find((category) => category.category === name).skills.length;

  return (
    <section className="relative overflow-hidden py-20" id="skills">
      {/* Decorative elements */}
      <div className="absolute left-10 top-20 h-40 w-40 animate-blob-slow rounded-full bg-blue-200 opacity-20 mix-blend-multiply blur-xl"></div>
      <div className="absolute bottom-10 right-10 h-32 w-32 animate-blob-medium rounded-full bg-indigo-200 opacity-20 mix-blend-multiply blur-xl"></div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading eyebrow="Toolkit" title="My Skills" className="mb-6">
          A collection of technologies and tools I've mastered throughout my software development
          journey.
        </SectionHeading>

        <motion.p
          className="mb-10 text-center text-xs tracking-wide text-slate-500"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
        >
          <span className="tabular-nums">{totalSkills}</span> technologies
          <span className="mx-2 text-slate-300">/</span>
          <span className="tabular-nums">{skillsData.length}</span> disciplines
        </motion.p>

        {/* Category filter */}
        <motion.div
          className="mb-12 flex flex-wrap justify-center gap-2"
          variants={stagger(0.06)}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
        >
          {filters.map((name) => {
            const isActive = name === activeCategory;
            return (
              <motion.button
                key={name}
                type="button"
                onClick={() => setActiveCategory(name)}
                aria-pressed={isActive}
                variants={scaleIn}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.96 }}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
                  isActive
                    ? 'border-transparent bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-md'
                    : 'border-slate-200 bg-white/80 text-slate-600 hover:border-indigo-300 hover:text-indigo-700'
                }`}
              >
                {name}
                <span
                  className={`ml-2 font-mono text-[11px] tabular-nums ${
                    isActive ? 'text-blue-100' : 'text-slate-400'
                  }`}
                >
                  {countFor(name)}
                </span>
              </motion.button>
            );
          })}
        </motion.div>

        <AnimatePresence mode="popLayout">
          {visibleCategories.map((category) => (
            <motion.div key={category.category} layout className="mb-14 last:mb-0">
              {/* Category header: name, count, and a rule that carries the eye across */}
              <motion.div
                className="mb-6 flex items-center gap-4"
                variants={stagger(0.08)}
                initial="hidden"
                whileInView="visible"
                viewport={viewport}
              >
                <motion.h3
                  variants={fadeUp}
                  className="whitespace-nowrap text-lg font-bold tracking-tight text-slate-800 sm:text-xl"
                >
                  {category.category}
                </motion.h3>
                <motion.span variants={fadeUp} className="font-mono text-xs tabular-nums text-slate-400">
                  {String(category.skills.length).padStart(2, '0')}
                </motion.span>
                <motion.span
                  variants={{
                    hidden: { scaleX: 0 },
                    visible: { scaleX: 1, transition: { duration: 0.7, ease: EASE } },
                  }}
                  style={{ transformOrigin: 'left' }}
                  className="h-px flex-1 bg-gradient-to-r from-slate-300 to-transparent"
                />
              </motion.div>

              <motion.div
                layout
                className={`grid gap-4 ${
                  category.banner
                    ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
                    : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'
                }`}
                variants={stagger(0.06)}
                initial="hidden"
                whileInView="visible"
                viewport={viewport}
              >
                {category.skills.map((skill) => (
                  <SkillTile key={skill.name} skill={skill} banner={category.banner} />
                ))}
              </motion.div>
            </motion.div>
          ))}
        </AnimatePresence>

        <motion.div
          className="mt-14 text-center"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
        >
          <a
            href="#projects"
            className="group inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-3 font-medium text-white shadow-md transition-all duration-300 hover:shadow-lg hover:shadow-indigo-500/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
          >
            See these in use
            <svg
              className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7-7 7M3 12h18" />
            </svg>
          </a>
        </motion.div>
      </div>
    </section>
  );
}

export default SkillsContainer;
